"use client"

/**
 * The on-screen keyboard for a touch-screen till (2026-10-02, GIOSHOP).
 *
 * Off unless THIS device switched it on (Settings → გარეგნობა). When on, a tap
 * on a field brings up our keyboard instead of Windows' one: a number pad for
 * amounts, quantities and PINs, Georgian (or English) letters for searching.
 * One component for every field in the app — it watches taps on the document,
 * so no screen had to be rewired. The arithmetic lives in lib/touch-keyboard.ts.
 *
 * How it stays out of the way:
 * - inputmode="none" is set on the tapped field BEFORE it takes focus, so the
 *   system keyboard does not come up as well; the field's own inputmode comes
 *   back when it loses focus.
 * - The keys never take focus (touchstart and mousedown are cancelled on the
 *   keyboard), so the caret stays in the field and a dialog's focus trap never
 *   sees the focus leave.
 * - The keyboard is a NEW element on every appearance. A Base UI dialog closes
 *   on a press outside itself — except on an element added to the page after
 *   it opened (its „third-party" rule, floating-ui useDismiss). A keyboard that
 *   existed before the dialog would close the sale dialog on the first key.
 * - It types the way a person does: the value goes in through the native
 *   setter plus an `input` event, so React's onChange runs as for a keystroke;
 *   „შეყვანა" is a real Enter keydown, so it does exactly what Enter does in
 *   that field (the sale dialog's Enter sells, the search's adds the product).
 * - A number pad opens by itself only for a number field a DIALOG focuses on
 *   opening (the sale's „received", a PIN prompt). A tap opens it everywhere
 *   else; letters always wait for a tap, so a till that refocuses its search
 *   after every scan does not get a keyboard after every scan.
 */

import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ArrowBigUp, ChevronDown, CornerDownLeft, Delete } from 'lucide-react'
import { cn } from '@/lib/utils'
import { useT } from '@/lib/i18n'
import {
  DIGIT_ROW, EN_ROWS, KA_ROWS, KA_SHIFT, TOUCH_KB_EVENT, TOUCH_KB_HOST, TOUCH_KB_STORAGE_KEY,
  allowsDot, enterValue, getTouchKeyboard, glyph, kindOf, liveValue, nextDraft, padLabel, placeNumpad, shownValue, textEdit,
  type FieldDesc, type KbKind, type NumKey, type Rect,
} from '@/lib/touch-keyboard'
import { bringIntoSight } from '@/lib/field-in-sight'

type Field = HTMLInputElement | HTMLTextAreaElement

/** The dialogs a number pad may come up with on its own. */
const POPUP = '[data-slot="dialog-content"], [data-slot="alert-dialog-content"]'
const PAD_W = 380
const PAD_H = 420
const MARGIN = 16

const isField = (n: unknown): n is Field => n instanceof HTMLInputElement || n instanceof HTMLTextAreaElement

function describe(el: Field): FieldDesc {
  const own = el.dataset.vkbIm
  return {
    tag: el.tagName.toLowerCase(),
    type: el instanceof HTMLInputElement ? el.type.toLowerCase() : '',
    inputMode: own !== undefined ? own : (el.getAttribute('inputmode') ?? '').toLowerCase(),
    readOnly: el.readOnly,
    disabled: el.disabled,
    optOut: !!el.closest('[data-vkb="off"]'),
  }
}

/** Keep the system keyboard down: inputmode=none, remembering the field's own. */
function quiet(el: Field) {
  if (el.dataset.vkbIm === undefined) el.dataset.vkbIm = (el.getAttribute('inputmode') ?? '').toLowerCase()
  el.setAttribute('inputmode', 'none')
}

function unquiet(el: Field) {
  const own = el.dataset.vkbIm
  if (own === undefined) return
  if (own) el.setAttribute('inputmode', own)
  else el.removeAttribute('inputmode')
  delete el.dataset.vkbIm
}

/** As a keystroke would: the native setter (React's value tracker sees a change) + `input`. */
function writeValue(el: Field, v: string) {
  const proto = el instanceof HTMLTextAreaElement ? HTMLTextAreaElement.prototype : HTMLInputElement.prototype
  const set = Object.getOwnPropertyDescriptor(proto, 'value')?.set
  if (set) set.call(el, v)
  else el.value = v
  el.dispatchEvent(new Event('input', { bubbles: true }))
}

/** type=number and email have no selection API — the caret is the end. */
function caretOf(el: Field): [number, number] {
  const end = el.value.length
  try {
    const s = el.selectionStart
    const e = el.selectionEnd
    return s == null || e == null ? [end, end] : [s, e]
  } catch {
    return [end, end]
  }
}

function setCaret(el: Field, pos: number) {
  try { el.setSelectionRange(pos, pos) } catch { /* no selection API on this type */ }
}

const rectOf = (el: Element): Rect => {
  const r = el.getBoundingClientRect()
  return { left: r.left, top: r.top, right: r.right, bottom: r.bottom }
}


/**
 * The field's <label>: its own (for= / wrapping), else the one beside it — a
 * label and a field in one small box, the way most forms here are drawn
 * (`<div><label/><div><Icon/><Input/></div></div>`). Only a box holding this
 * ONE field counts, so a neighbour's label is never borrowed.
 */
function nearbyLabel(el: Field): string {
  const own = el.labels?.[0]
  if (own) return own.textContent ?? ''
  let box: Element | null = el.parentElement
  for (let i = 0; i < 3 && box; i++, box = box.parentElement) {
    if (box.querySelectorAll('input, textarea, select').length !== 1) break
    const l = box.querySelector('label')
    if (l) return l.textContent ?? ''
  }
  return ''
}

function placeFor(el: Field) {
  const popup = el.closest(POPUP)
  return placeNumpad({
    vw: window.innerWidth, vh: window.innerHeight, padW: PAD_W, padH: PAD_H, margin: MARGIN,
    field: rectOf(el), avoid: popup ? rectOf(popup) : undefined,
  })
}

const prevent = (e: Event) => e.preventDefault()

export function TouchKeyboard() {
  const t = useT()
  const [enabled, setEnabled] = useState(false)
  const [field, setField] = useState<Field | null>(null)
  const [host, setHost] = useState<HTMLDivElement | null>(null)
  const [kind, setKind] = useState<KbKind>('numeric')
  const [layout, setLayout] = useState<'ka' | 'en'>('ka')
  const [shift, setShift] = useState(false)
  const [display, setDisplay] = useState('')
  const [label, setLabel] = useState('')
  const [place, setPlace] = useState<{ side: 'left' | 'right'; reserve: boolean }>({ side: 'right', reserve: false })

  const panelRef = useRef<HTMLDivElement | null>(null)
  const hostRef = useRef<HTMLDivElement | null>(null)
  /** the field in our care: inputmode none, possibly with the keyboard hidden */
  const trackedRef = useRef<Field | null>(null)
  /** the field the keyboard is showing for */
  const shownRef = useRef<Field | null>(null)
  /** tapped, not focused yet — a touch focuses on the tap's end */
  const pendingRef = useRef<Field | null>(null)
  const pressingRef = useRef(false)
  const kindRef = useRef<KbKind>('numeric')
  /** the pad's reading of the field; `written` = what the field held after our last key */
  const numRef = useRef<{ el: Field | null; draft: string | null; written: string }>({ el: null, draft: null, written: '' })
  const repeatRef = useRef<{ wait?: number; tick?: number }>({})

  // The device setting, and its changes from Settings or another tab.
  useEffect(() => {
    const sync = () => setEnabled(getTouchKeyboard())
    sync()
    const onStorage = (e: StorageEvent) => { if (e.key === TOUCH_KB_STORAGE_KEY) sync() }
    window.addEventListener(TOUCH_KB_EVENT, sync)
    window.addEventListener('storage', onStorage)
    return () => {
      window.removeEventListener(TOUCH_KB_EVENT, sync)
      window.removeEventListener('storage', onStorage)
    }
  }, [])

  const stopRepeat = useCallback(() => {
    window.clearTimeout(repeatRef.current.wait)
    window.clearInterval(repeatRef.current.tick)
    repeatRef.current = {}
  }, [])

  const startRepeat = useCallback((fn: () => void) => {
    stopRepeat()
    repeatRef.current.wait = window.setTimeout(() => {
      repeatRef.current.tick = window.setInterval(fn, 70)
    }, 450)
  }, [stopRepeat])

  const track = useCallback((el: Field) => {
    const prev = trackedRef.current
    if (prev && prev !== el) unquiet(prev)
    quiet(el)
    trackedRef.current = el
  }, [])

  const show = useCallback((el: Field, k: KbKind) => {
    track(el)
    shownRef.current = el
    kindRef.current = k
    numRef.current = { el, draft: null, written: el.value }
    setKind(k)
    setShift(false)
    setDisplay(el.value)
    setLabel(padLabel({
      aria: el.getAttribute('aria-label') ?? '',
      label: nearbyLabel(el),
      placeholder: el instanceof HTMLInputElement ? el.placeholder : '',
    }))
    if (k === 'numeric') setPlace(placeFor(el))
    if (!hostRef.current) {
      const h = document.createElement('div')
      h.setAttribute(TOUCH_KB_HOST, '')
      // No tap gesture (touch) and no mousedown default (mouse) → the keys never take focus.
      h.addEventListener('touchstart', prevent, { passive: false })
      h.addEventListener('mousedown', prevent)
      h.addEventListener('pointerdown', () => { pressingRef.current = true }, true)
      document.body.appendChild(h)
      hostRef.current = h
      setHost(h)
    }
    setField(el)
  }, [track])

  const hide = useCallback(() => {
    stopRepeat()
    shownRef.current = null
    setField(null)
    const h = hostRef.current
    if (h) {
      h.remove()
      hostRef.current = null
      setHost(null)
    }
  }, [stopRepeat])

  const drop = useCallback(() => {
    hide()
    const el = trackedRef.current
    if (el) unquiet(el)
    trackedRef.current = null
    pendingRef.current = null
  }, [hide])

  /** The field, if it is still on the page — a sale that completed took its dialog with it. */
  const live = useCallback((): Field | null => {
    const el = shownRef.current
    if (el && el.isConnected) return el
    drop()
    return null
  }, [drop])

  // Taps, focus and blur — only while the device has it switched on.
  useEffect(() => {
    if (!enabled) return
    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return
      const target = e.target as Element | null
      if (!target || hostRef.current?.contains(target)) return
      const el = target.closest?.('input, textarea')
      if (!isField(el)) return
      const k = kindOf(describe(el))
      if (!k) return
      track(el)
      if (document.activeElement === el) {
        pendingRef.current = null
        show(el, k)
      } else {
        pendingRef.current = el
      }
    }
    const onFocusIn = (e: FocusEvent) => {
      const el = e.target
      if (!isField(el)) return
      if (pendingRef.current === el) {
        pendingRef.current = null
        const k = kindOf(describe(el))
        if (k) show(el, k)
        return
      }
      // A dialog that focuses its amount on opening: the pad comes with it.
      if (!shownRef.current && el.closest(POPUP) && kindOf(describe(el)) === 'numeric') show(el, 'numeric')
    }
    const onFocusOut = (e: FocusEvent) => {
      const el = trackedRef.current
      if (!el || e.target !== el) return
      const to = e.relatedTarget as Node | null
      if (pressingRef.current && shownRef.current === el && (!to || hostRef.current?.contains(to))) {
        // A key must never take the field's focus; should one slip through,
        // give it back. Focus sent somewhere real (a handler's choice) stays.
        requestAnimationFrame(() => { if (el.isConnected && trackedRef.current === el) el.focus({ preventScroll: true }) })
        return
      }
      requestAnimationFrame(() => {
        if (trackedRef.current === el && document.activeElement !== el) drop()
      })
    }
    const onPointerEnd = () => {
      stopRepeat()
      window.setTimeout(() => { pressingRef.current = false }, 0)
    }
    document.addEventListener('pointerdown', onPointerDown, true)
    document.addEventListener('focusin', onFocusIn, true)
    document.addEventListener('focusout', onFocusOut, true)
    window.addEventListener('pointerup', onPointerEnd, true)
    window.addEventListener('pointercancel', onPointerEnd, true)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown, true)
      document.removeEventListener('focusin', onFocusIn, true)
      document.removeEventListener('focusout', onFocusOut, true)
      window.removeEventListener('pointerup', onPointerEnd, true)
      window.removeEventListener('pointercancel', onPointerEnd, true)
      drop()
    }
  }, [enabled, track, show, drop, stopRepeat])

  // The field went away while the keyboard was up (a sale completed, a dialog closed).
  useEffect(() => {
    if (!field) return
    const mo = new MutationObserver(() => { if (!field.isConnected) drop() })
    mo.observe(document.body, { childList: true, subtree: true })
    const onResize = () => { if (kindRef.current === 'numeric' && field.isConnected) setPlace(placeFor(field)) }
    window.addEventListener('resize', onResize)
    return () => {
      mo.disconnect()
      window.removeEventListener('resize', onResize)
    }
  }, [field, drop])

  // Hold the bottom of the screen when the keyboard spans it (globals.css moves
  // dialogs up), then make sure the field is not under it.
  useLayoutEffect(() => {
    const root = document.documentElement
    const panel = panelRef.current
    if (!field || !panel) {
      root.removeAttribute('data-vkb-reserve')
      root.style.removeProperty('--vkb-h')
      return
    }
    if (kind === 'text' || place.reserve) {
      root.setAttribute('data-vkb-reserve', '')
      root.style.setProperty('--vkb-h', `${panel.offsetHeight + (kind === 'numeric' ? MARGIN : 0)}px`)
    } else {
      root.removeAttribute('data-vkb-reserve')
      root.style.removeProperty('--vkb-h')
    }
    let inner = 0
    const outer = requestAnimationFrame(() => {
      inner = requestAnimationFrame(() => {
        // Clear of the pad now; the dialog shrinking above it over the next
        // 300 ms is followed by keepFocusedFieldInSight (lib/field-in-sight.ts).
        if (field.isConnected && panelRef.current) bringIntoSight(field, rectOf(panelRef.current))
      })
    })
    return () => {
      cancelAnimationFrame(outer)
      cancelAnimationFrame(inner)
    }
  }, [field, kind, place, layout, host])

  useEffect(() => () => {
    document.documentElement.removeAttribute('data-vkb-reserve')
    document.documentElement.style.removeProperty('--vkb-h')
  }, [])

  // ── Keys ──────────────────────────────────────────────────────────────────

  const pressNum = (key: NumKey) => {
    const el = live()
    if (!el) return
    const number = el instanceof HTMLInputElement && el.type === 'number'
    const st = numRef.current
    const fresh = st.el !== el || st.written !== el.value
    const d = nextDraft(fresh ? null : st.draft, el.value, key, {
      dot: allowsDot({ type: el instanceof HTMLInputElement ? el.type : '', inputMode: describe(el).inputMode, step: el.getAttribute('step') ?? '' }),
      number,
      maxLength: el.maxLength > 0 ? el.maxLength : undefined,
    })
    if (d === null) return
    const v = liveValue(d, number)
    if (v !== null && v !== el.value) writeValue(el, v)
    numRef.current = { el, draft: d, written: el.value }
    setDisplay(d)
  }

  const pressText = (edit: string | 'back') => {
    const el = live()
    if (!el) return
    const [a, b] = caretOf(el)
    const r = textEdit(el.value, a, b, edit === 'back' ? 'back' : { insert: edit }, el.maxLength > 0 ? el.maxLength : undefined)
    if (!r) return
    writeValue(el, r.value)
    setCaret(el, r.caret)
  }

  const pressLetter = (base: string) => {
    pressText(glyph(layout, base, shift))
    if (shift) setShift(false)
  }

  const pressEnter = () => {
    const el = live()
    if (!el) return
    if (el instanceof HTMLTextAreaElement) { pressText('\n'); return }
    const st = numRef.current
    if (kindRef.current === 'numeric' && st.el === el && st.draft !== null && st.written === el.value) {
      const v = enterValue(st.draft, el.type === 'number')
      if (v !== el.value) writeValue(el, v)
    }
    hide()
    const go = el.dispatchEvent(new KeyboardEvent('keydown', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: true }))
    el.dispatchEvent(new KeyboardEvent('keyup', { key: 'Enter', code: 'Enter', bubbles: true, cancelable: true }))
    if (go && el.form) {
      try { el.form.requestSubmit() } catch { /* a form that cannot submit stays as it is */ }
    }
  }

  if (!enabled || !field || !host) return null

  // a PIN on the pad's display is dots — the screen faces the customer
  const masked = field instanceof HTMLInputElement && field.type === 'password'

  const backKey = (onPress: () => void, className?: string, grow?: number) => (
    <Key label={t('vkb.back')} onPress={onPress} onHold={() => startRepeat(onPress)} className={cn(SPECIAL, className)} grow={grow}>
      <Delete className="size-6" />
    </Key>
  )

  const pad = kind === 'numeric' ? (
    <div
      ref={panelRef}
      role="group"
      aria-label={t('vkb.aria')}
      className={cn(
        'fixed z-[60] select-none touch-none rounded-[28px] border border-border bg-card p-3 shadow-[0_24px_60px_-20px_rgb(0_0_0/0.45)]',
        'bottom-4 w-[380px]', place.side === 'right' ? 'right-4' : 'left-4',
        'max-md:inset-x-0 max-md:bottom-0 max-md:w-auto max-md:rounded-b-none max-md:pb-[calc(env(safe-area-inset-bottom)+0.75rem)]',
      )}
    >
      <div className="flex items-end justify-between gap-3 px-2 pt-1 pb-3">
        <span className="min-w-0 truncate text-[12px] font-semibold text-muted-foreground">{label}</span>
        <span className={cn('shrink-0 text-[32px] leading-none font-black tabular-nums', display === '' ? 'text-muted-foreground' : 'text-foreground')}>
          {display === '' ? (masked ? '' : '0') : shownValue(display, masked)}
        </span>
      </div>
      <div className="grid grid-cols-4 gap-3">
        {(['1', '2', '3'] as const).map((d) => <Key key={d} label={d} onPress={() => pressNum(d)} className={DIGIT}>{d}</Key>)}
        {backKey(() => pressNum('back'), 'h-[72px]')}
        {(['4', '5', '6'] as const).map((d) => <Key key={d} label={d} onPress={() => pressNum(d)} className={DIGIT}>{d}</Key>)}
        <Key label={t('vkb.clear')} onPress={() => pressNum('clear')} className={cn(SPECIAL, 'h-[72px] text-2xl font-black')}>C</Key>
        {(['7', '8', '9'] as const).map((d) => <Key key={d} label={d} onPress={() => pressNum(d)} className={DIGIT}>{d}</Key>)}
        <Key label={t('vkb.enter')} onPress={pressEnter} className={cn(ENTER, 'row-span-2 flex-col gap-1.5 text-[15px]')}>
          <CornerDownLeft className="size-6" />
          {t('vkb.enter')}
        </Key>
        {numDot(field)
          ? <Key label="." onPress={() => pressNum('.')} className={DIGIT}>.</Key>
          : <span aria-hidden />}
        <Key label="0" onPress={() => pressNum('0')} className={DIGIT}>0</Key>
        <Key label={t('vkb.hide')} onPress={hide} className={cn(SPECIAL, 'h-[72px]')}>
          <ChevronDown className="size-6" />
        </Key>
      </div>
    </div>
  ) : (
    <div
      ref={panelRef}
      role="group"
      aria-label={t('vkb.aria')}
      className="fixed inset-x-0 bottom-0 z-[60] select-none touch-none border-t border-border bg-card shadow-[0_-16px_48px_-24px_rgb(0_0_0/0.35)]"
    >
      <div className="mx-auto flex max-w-[1080px] flex-col gap-1.5 px-2 pt-2 pb-[calc(env(safe-area-inset-bottom)+0.5rem)]">
        <Row>
          {DIGIT_ROW.map((d) => <Key key={d} label={d} onPress={() => pressText(d)} className={LETTER} grow={1}>{d}</Key>)}
        </Row>
        {(layout === 'ka' ? KA_ROWS : EN_ROWS).map((row, i) => (
          <Row key={i} className={i === 1 ? 'px-[4.5%]' : undefined}>
            {i === 2 && (
              <Key label={t('vkb.shift')} onPress={() => setShift((s) => !s)} grow={1.5}
                className={cn(SPECIAL, 'h-12', shift && 'bg-primary/15 text-primary hover:bg-primary/20')}>
                <ArrowBigUp className="size-6" />
              </Key>
            )}
            {row.map((base) => (
              <Key key={base} label={glyph(layout, base, shift)} onPress={() => pressLetter(base)} className={cn(LETTER, 'relative')} grow={1}>
                {glyph(layout, base, shift)}
                {layout === 'ka' && !shift && KA_SHIFT[base] && (
                  <span aria-hidden className="absolute top-0.5 right-1.5 text-[12px] font-semibold text-muted-foreground">{KA_SHIFT[base]}</span>
                )}
              </Key>
            ))}
            {i === 2 && backKey(() => pressText('back'), 'h-12', 1.5)}
          </Row>
        ))}
        <Row>
          <Key label={layout === 'ka' ? 'ENG' : 'ქარ'} onPress={() => { setLayout((l) => (l === 'ka' ? 'en' : 'ka')); setShift(false) }}
            className={cn(SPECIAL, 'h-12 text-[15px] font-black')} grow={1.5}>
            {layout === 'ka' ? 'ENG' : 'ქარ'}
          </Key>
          <Key label="," onPress={() => pressText(',')} className={LETTER} grow={1}>,</Key>
          <Key label={t('vkb.space')} onPress={() => pressText(' ')} className={LETTER} grow={4.5}>{' '}</Key>
          <Key label="." onPress={() => pressText('.')} className={LETTER} grow={1}>.</Key>
          <Key label="-" onPress={() => pressText('-')} className={LETTER} grow={1}>-</Key>
          <Key label={t('vkb.enter')} onPress={pressEnter} className={cn(ENTER, 'h-12 gap-2 text-[15px]')} grow={2}>
            <CornerDownLeft className="size-5" />
            {t('vkb.enter')}
          </Key>
          <Key label={t('vkb.hide')} onPress={hide} className={cn(SPECIAL, 'h-12')} grow={1}>
            <ChevronDown className="size-6" />
          </Key>
        </Row>
      </div>
    </div>
  )

  return createPortal(pad, host)
}

/** Whether the number pad shows its point key for this field. */
function numDot(el: Field): boolean {
  return allowsDot({
    type: el instanceof HTMLInputElement ? el.type : '',
    inputMode: describe(el).inputMode,
    step: el.getAttribute('step') ?? '',
  })
}

const KEY = 'flex items-center justify-center rounded-xl font-bold transition-colors duration-75 active:scale-[0.97] disabled:opacity-40'
const DIGIT = 'h-[72px] bg-muted/70 text-3xl text-foreground tabular-nums hover:bg-muted active:bg-muted'
const LETTER = 'h-12 bg-muted/70 text-xl text-foreground hover:bg-muted active:bg-muted'
const SPECIAL = 'bg-muted text-muted-foreground hover:text-foreground active:bg-muted/80'
const ENTER = 'bg-primary text-primary-foreground hover:bg-primary/90 active:bg-primary/85'

function Row({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={cn('flex gap-1.5', className)}>{children}</div>
}

/**
 * A key acts on pointerdown — at once, as a hardware key does — and cancels
 * the default there too, so the field keeps its focus. Never focusable.
 */
function Key({ label, onPress, onHold, className, grow, children }: {
  label: string
  onPress: () => void
  onHold?: () => void
  className?: string
  grow?: number
  children: ReactNode
}) {
  return (
    <button
      type="button"
      tabIndex={-1}
      aria-label={label}
      onPointerDown={(e) => {
        if (e.button !== 0) return
        e.preventDefault()
        onPress()
        onHold?.()
      }}
      style={grow ? { flexGrow: grow, flexBasis: 0 } : undefined}
      className={cn(KEY, className)}
    >
      {children}
    </button>
  )
}
